import { ROLES } from './roles.js';
/**
 * Every distinct thing a role can be allowed to do.
 *
 * A permission answers "what kind of request may this role make". It does not
 * answer "how far does that request reach" — a restaurant manager holding
 * `order.view.restaurant` still only ever sees their own restaurant's orders.
 * Handlers narrow by tenant, department or restaurant on top of this check.
 */
export const PERMISSIONS = [
    // Organisation and accounts
    'org.manage', // sites, departments, delivery points
    'user.manage', // invite, change role, deactivate
    'user.pii.read', // decrypt a contact detail (audited separately)
    'guest.manage', // invite guests, manage their dates
    // Restaurants and menus
    'restaurant.manage', // register and link restaurants
    'partner.manage', // ask for, accept and end a trading relationship
    'menu.view',
    'menu.manage', // items, weekly schedule, publish
    'cutoff.manage', // cutoff rules
    // Orders
    'order.place', // order for yourself
    'order.cancel.own', // cancel your own before the cutoff
    'order.manual_add', // record a phone order placed after the cutoff
    'order.override', // post-cutoff change, reason required
    'order.claim.create', // restaurant records an extra delivery
    'order.claim.review', // company accepts or rejects that record
    'order.deliver', // mark a confirmed meal as handed over
    'order.view.own',
    'order.view.department',
    'order.view.company',
    'order.view.restaurant',
    // The subscription itself: the band, the term, and whether the app carries
    // advertising. Held by whoever pays, which is both sides in a marketplace —
    // so this is not `org.manage`, which a kitchen does not have.
    'billing.manage',
    // Settlement and statistics
    'settlement.manage',
    'settlement.view.restaurant',
    'stats.view',
    // Files and system
    'file.upload',
    'file.delete',
    'audit.view',
    'system.manage', // settings, backups, diagnostics
];
const EMPLOYEE_BASE = [
    'menu.view',
    'order.place',
    'order.cancel.own',
    'order.view.own',
];
/**
 * The authoritative grant table. §4.3 of the development plan is this object in
 * prose; the integration tests walk it row by row.
 */
export const ROLE_PERMISSIONS = {
    super_admin: PERMISSIONS,
    company_admin: [
        'org.manage',
        'user.manage',
        'user.pii.read',
        'guest.manage',
        'restaurant.manage',
        'partner.manage',
        'menu.view',
        ...EMPLOYEE_BASE.filter((p) => p !== 'menu.view'),
        'order.manual_add',
        'order.override',
        'order.claim.review',
        'order.deliver',
        'order.view.company',
        'billing.manage',
        'settlement.manage',
        'stats.view',
        'file.upload',
        'file.delete',
        'audit.view',
    ],
    // Runs their own department: invites guests, watches their people's orders.
    // No settlement, no company-wide view, no manual entry — those create billing
    // obligations and belong to whoever owns the budget.
    dept_admin: [
        'guest.manage',
        ...EMPLOYEE_BASE,
        'order.view.department',
        'stats.view',
    ],
    employee: EMPLOYEE_BASE,
    guest: EMPLOYEE_BASE,
    restaurant_admin: [
        'partner.manage',
        'menu.view',
        'menu.manage',
        'cutoff.manage',
        'order.view.restaurant',
        'order.override',
        'order.claim.create',
        'order.deliver',
        'billing.manage',
        'settlement.view.restaurant',
        'file.upload',
        'file.delete',
    ],
};
// Set lookup rather than array scanning: guards run on every request.
const GRANTS = {};
for (const role of ROLES) {
    GRANTS[role] = new Set(ROLE_PERMISSIONS[role]);
}
export function hasPermission(role, permission) {
    return GRANTS[role].has(permission);
}
export function hasAnyPermission(role, permissions) {
    return permissions.some((p) => hasPermission(role, p));
}
/** Widest order visibility the role holds, or null if it sees none. */
export function orderVisibility(role) {
    if (hasPermission(role, 'order.view.company'))
        return 'company';
    if (hasPermission(role, 'order.view.restaurant'))
        return 'restaurant';
    if (hasPermission(role, 'order.view.department'))
        return 'department';
    if (hasPermission(role, 'order.view.own'))
        return 'own';
    return null;
}
//# sourceMappingURL=permissions.js.map